import { formatDistanceToNow } from "date-fns";
import { ActivityIcon, AlertTriangleIcon, CircleIcon } from "lucide-react";

import { Skeleton } from "@/components/ui/skeleton";
import { useSecurityEvents } from "@/hooks/queries/securityQueries";
import { cn } from "@/lib/utils";
import { SecuritySection } from "./Section";
import { eventDevice, eventText, isWarningEvent } from "./describe";

export const ActivitySection = () => {
  const { data: events, isLoading, isError } = useSecurityEvents();

  return (
    <SecuritySection
      icon={ActivityIcon}
      title="Recent security activity"
      description="Sign-ins, password changes and other account events from the last 90 days."
    >
      {isLoading ? (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-10 rounded-lg" />
          <Skeleton className="h-10 rounded-lg" />
          <Skeleton className="h-10 rounded-lg" />
        </div>
      ) : isError ? (
        <p className="text-muted-foreground text-sm">
          Activity is not available right now.
        </p>
      ) : !events?.length ? (
        <p className="text-muted-foreground text-sm">No activity yet.</p>
      ) : (
        <ol className="border-border/60 relative ml-2 flex flex-col gap-4 border-l pl-5">
          {events.map(event => {
            const warning = isWarningEvent(event);
            const device = eventDevice(event);
            return (
              <li key={event.id} className="relative">
                <span className="bg-background absolute top-0.5 -left-[27px] flex size-4 items-center justify-center">
                  {warning ? (
                    <AlertTriangleIcon className="size-3.5 text-amber-400" />
                  ) : (
                    <CircleIcon className="size-2.5 fill-current text-emerald-400" />
                  )}
                </span>
                <div className="flex flex-col gap-0.5 sm:flex-row sm:items-baseline sm:justify-between sm:gap-4">
                  <span
                    className={cn(
                      "text-sm",
                      warning ? "text-amber-200" : "text-white",
                    )}
                  >
                    {eventText(event)}
                    {event.currentSession ? (
                      <span className="text-muted-foreground">
                        {" "}
                        · this device
                      </span>
                    ) : null}
                  </span>
                  <span className="text-muted-foreground shrink-0 text-xs">
                    {formatDistanceToNow(new Date(event.createdAt), {
                      addSuffix: true,
                    })}
                  </span>
                </div>
                {device || event.ipAddress ? (
                  <p className="text-muted-foreground text-xs">
                    {[device, event.ipAddress ? `IP ${event.ipAddress}` : null]
                      .filter(Boolean)
                      .join(" · ")}
                  </p>
                ) : null}
              </li>
            );
          })}
        </ol>
      )}
    </SecuritySection>
  );
};
